import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { db } from "../../firebase";
import { calculatePoints } from "../../utils/customerEntryPoints";

const COLLECTION = "customer_reward_gifts";
const DEFAULT_GIFTS = [
  { giftName: "Refrigerator", pointsRequired: 1000 },
  { giftName: "Washing machine", pointsRequired: 750 },
  { giftName: "Mobile phone", pointsRequired: 500 },
  { giftName: "Gas stove", pointsRequired: 250 },
  { giftName: "Dinner set", pointsRequired: 100 },
  { giftName: "Gift box", pointsRequired: 50 },
];

export default function CustomerGiftSettingsPage() {
  const [rows, setRows] = useState([]);
  const [entryDocs, setEntryDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [giftName, setGiftName] = useState("");
  const [points, setPoints] = useState("");
  const [price, setPrice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [previewPoints, setPreviewPoints] = useState("");
  const [recipientGift, setRecipientGift] = useState(null);
  const [recipientSearch, setRecipientSearch] = useState("");

  React.useEffect(() => {
    const qy = query(collection(db, COLLECTION), orderBy("pointsRequired", "desc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setRows(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Could not load gift settings");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  React.useEffect(() => {
    if (!recipientGift) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeRecipients();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [recipientGift]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "customer_entries"));
        if (cancelled) return;
        setEntryDocs(
          snap.docs.map((d) => {
            const data = d.data() || {};
            return {
              customerName: data.customerName,
              phone: data.phone,
              points: data.points,
              amount: data.amount,
              voided: data.voided,
            };
          })
        );
      } catch {
        if (!cancelled) setEntryDocs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCount = useMemo(() => rows.filter((r) => r.active !== false).length, [rows]);
  const inactiveCount = rows.length - activeCount;
  const highestThreshold = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Number(r.pointsRequired) || 0), 0),
    [rows]
  );
  const giftRecipientsById = useMemo(() => {
    const active = rows
      .filter((r) => r.active !== false)
      .map((r) => ({
        id: r.id,
        giftName: r.giftName || "",
        pointsRequired: Number(r.pointsRequired) || 0,
      }))
      .filter((r) => r.pointsRequired > 0)
      .sort((a, b) => b.pointsRequired - a.pointsRequired);
    const lists = {};
    for (const g of active) lists[g.id] = [];
    if (!active.length || !entryDocs.length) return lists;

    const pointsByCustomer = new Map();
    for (const data of entryDocs) {
      if (data.voided === true) continue;
      const name = String(data.customerName || "").trim();
      if (!name) continue;
      const pts =
        data.points != null && data.points !== "" ? Number(data.points) || 0 : calculatePoints(Number(data.amount) || 0);
      const phone = String(data.phone || "").trim();
      const prev = pointsByCustomer.get(name) || { name, points: 0, phone: "", visits: 0 };
      prev.points += pts;
      prev.visits += 1;
      if (phone) prev.phone = phone;
      pointsByCustomer.set(name, prev);
    }
    for (const rec of pointsByCustomer.values()) {
      const hit = active.find((g) => rec.points >= g.pointsRequired);
      if (!hit) continue;
      lists[hit.id].push(rec);
    }
    for (const id of Object.keys(lists)) {
      lists[id].sort((a, b) => b.points - a.points || a.name.localeCompare(b.name));
    }
    return lists;
  }, [rows, entryDocs]);
  const giftReceivedCounts = useMemo(() => {
    const counts = {};
    for (const [id, list] of Object.entries(giftRecipientsById)) {
      counts[id] = list.length;
    }
    return counts;
  }, [giftRecipientsById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.giftName || "").toLowerCase();
      const pts = String(Number(r.pointsRequired) || 0);
      return name.includes(q) || pts.includes(q);
    });
  }, [rows, search]);

  const previewReward = useMemo(() => {
    const p = Number(previewPoints) || 0;
    if (!p) return null;
    const active = rows
      .filter((r) => r.active !== false)
      .sort((a, b) => (Number(b.pointsRequired) || 0) - (Number(a.pointsRequired) || 0));
    return active.find((r) => p >= (Number(r.pointsRequired) || 0)) || null;
  }, [rows, previewPoints]);
  const totalPriceIntoReceived = useMemo(() => {
    return rows.reduce((sum, r) => {
      const rowPrice = Number(r.price) || 0;
      const received = giftReceivedCounts[r.id] || 0;
      return sum + rowPrice * received;
    }, 0);
  }, [rows, giftReceivedCounts]);
  const totalReceivedPeople = useMemo(
    () => rows.reduce((sum, r) => sum + (giftReceivedCounts[r.id] || 0), 0),
    [rows, giftReceivedCounts]
  );
  const totalGiftPrices = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.price) || 0), 0),
    [rows]
  );
  const avgGiftPrice = useMemo(() => (rows.length ? totalGiftPrices / rows.length : 0), [rows.length, totalGiftPrices]);
  const topReceivedGift = useMemo(() => {
    let best = null;
    for (const r of rows) {
      const received = giftReceivedCounts[r.id] || 0;
      if (!best || received > best.received) {
        best = { giftName: r.giftName || "-", received };
      }
    }
    return best;
  }, [rows, giftReceivedCounts]);
  const topValueGift = useMemo(() => {
    let best = null;
    for (const r of rows) {
      const rowPrice = Number(r.price) || 0;
      const received = giftReceivedCounts[r.id] || 0;
      const value = rowPrice * received;
      if (!best || value > best.value) {
        best = { giftName: r.giftName || "-", value };
      }
    }
    return best;
  }, [rows, giftReceivedCounts]);
  const neverReceivedCount = useMemo(
    () => rows.filter((r) => (giftReceivedCounts[r.id] || 0) === 0).length,
    [rows, giftReceivedCounts]
  );

  const recipientList = useMemo(() => {
    if (!recipientGift) return [];
    const all = giftRecipientsById[recipientGift.id] || [];
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => {
      const name = String(r.name || "").toLowerCase();
      const phone = String(r.phone || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || String(r.points).includes(q);
    });
  }, [recipientGift, giftRecipientsById, recipientSearch]);

  const openRecipients = (row) => {
    setRecipientGift(row);
    setRecipientSearch("");
  };

  const closeRecipients = () => {
    setRecipientGift(null);
    setRecipientSearch("");
  };

  const resetForm = () => {
    setGiftName("");
    setPoints("");
    setPrice("");
    setEditingId(null);
  };

  const saveGift = async () => {
    const name = String(giftName || "").trim();
    const pts = Math.max(0, Number(points) || 0);
    const giftPrice = Math.max(0, Number(price) || 0);
    if (!name) {
      toast.error("Enter gift name");
      return;
    }
    if (!pts) {
      toast.error("Enter required points");
      return;
    }
    if (giftPrice < 0) {
      toast.error("Enter valid gift price");
      return;
    }
    const duplicateByPoints = rows.find((r) => Number(r.pointsRequired) === pts && r.id !== editingId);
    if (duplicateByPoints) {
      toast.error(`Points ${pts} already mapped to "${duplicateByPoints.giftName}"`);
      return;
    }
    const duplicateByName = rows.find(
      (r) => String(r.giftName || "").trim().toLowerCase() === name.toLowerCase() && r.id !== editingId
    );
    if (duplicateByName) {
      toast.error(`Gift "${name}" already exists`);
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, COLLECTION, editingId), {
          giftName: name,
          pointsRequired: pts,
          price: giftPrice,
          updatedAt: serverTimestamp(),
        });
        toast.success("Gift updated");
      } else {
        await addDoc(collection(db, COLLECTION), {
          giftName: name,
          pointsRequired: pts,
          price: giftPrice,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        toast.success("Gift added");
      }
      resetForm();
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setGiftName(String(row.giftName || ""));
    setPoints(String(row.pointsRequired || ""));
    setPrice(String(Number(row.price) || 0));
  };

  const toggleActive = async (row) => {
    try {
      await updateDoc(doc(db, COLLECTION, row.id), {
        active: row.active === false,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      toast.error("Could not update status");
    }
  };

  const deleteGift = async (row) => {
    const ok = window.confirm(`Delete "${row.giftName}" (${Number(row.pointsRequired) || 0} pts)?`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, COLLECTION, row.id));
      if (editingId === row.id) resetForm();
      toast.success("Gift deleted");
    } catch (e) {
      console.error(e);
      toast.error("Could not delete gift");
    }
  };

  const seedDefaults = async () => {
    if (seedBusy) return;
    setSeedBusy(true);
    try {
      const existing = new Set(rows.map((r) => Number(r.pointsRequired) || 0));
      let added = 0;
      for (const g of DEFAULT_GIFTS) {
        if (existing.has(g.pointsRequired)) continue;
        await addDoc(collection(db, COLLECTION), {
          giftName: g.giftName,
          pointsRequired: g.pointsRequired,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        added += 1;
      }
      toast.success(added ? `Added ${added} default gifts` : "Default gift points already present");
    } catch (e) {
      console.error(e);
      toast.error("Could not add defaults");
    } finally {
      setSeedBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto h-[calc(100vh-84px)] max-w-7xl overflow-hidden px-4 py-4">
        <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Customer Entry</p>
                <h1 className="text-lg font-bold text-slate-900">Gift details & point settings</h1>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/reports/customer-entry"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back
                </Link>
                <button
                  type="button"
                  onClick={seedDefaults}
                  disabled={seedBusy}
                  className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                >
                  {seedBusy ? "Adding..." : "Insert defaults"}
                </button>
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 px-4 py-3">
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:grid-cols-8">
              <div className="rounded-lg bg-slate-100 px-2 py-1.5"><span className="text-slate-500">Gifts</span><p className="font-bold text-slate-900">{rows.length}</p></div>
              <div className="rounded-lg bg-emerald-50 px-2 py-1.5"><span className="text-emerald-700">Active</span><p className="font-bold text-emerald-800">{activeCount}</p></div>
              <div className="rounded-lg bg-rose-50 px-2 py-1.5"><span className="text-rose-700">Inactive</span><p className="font-bold text-rose-800">{inactiveCount}</p></div>
              <div className="rounded-lg bg-indigo-50 px-2 py-1.5"><span className="text-indigo-700">Top Threshold</span><p className="font-bold text-indigo-800">{highestThreshold || 0}</p></div>
              <div className="rounded-lg bg-slate-100 px-2 py-1.5"><span className="text-slate-500">Recipients</span><p className="font-bold text-slate-900">{totalReceivedPeople}</p></div>
              <div className="rounded-lg bg-slate-100 px-2 py-1.5"><span className="text-slate-500">Avg Price</span><p className="font-bold text-slate-900">₹{Math.round(avgGiftPrice).toLocaleString("en-IN")}</p></div>
              <div className="rounded-lg bg-violet-50 px-2 py-1.5"><span className="text-violet-700">Top Gift</span><p className="truncate font-bold text-violet-800">{topReceivedGift?.giftName || "-"}</p></div>
              <div className="rounded-lg bg-emerald-50 px-2 py-1.5"><span className="text-emerald-700">Value Impact</span><p className="font-bold text-emerald-800">₹{Math.round(totalPriceIntoReceived).toLocaleString("en-IN")}</p></div>
            </div>
          </div>

          <div className="border-b border-slate-200 px-4 py-3">
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-[1fr_10rem_10rem_13rem_13rem_auto]">
              <input
                type="text"
                value={giftName}
                onChange={(e) => setGiftName(e.target.value)}
                placeholder="Gift name"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="number"
                min="1"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="Points"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="number"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Price"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                type="number"
                min="0"
                value={previewPoints}
                onChange={(e) => setPreviewPoints(e.target.value)}
                placeholder="Points preview"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveGift}
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingId ? "Update" : "Add"}
                </button>
                {editingId ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Eligible gift for preview:{" "}
              <span className="font-semibold text-slate-800">
                {previewReward ? `${previewReward.giftName} (${previewReward.pointsRequired} pts)` : "No gift yet"}
              </span>
            </p>
          </div>

          <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {loading ? (
            <p className="px-4 py-6 text-sm text-slate-500">Loading...</p>
          ) : filteredRows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500">No gift settings yet.</p>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Gift</th>
                    <th className="px-4 py-3 text-right">Points needed</th>
                    <th className="px-4 py-3 text-right">Price (₹)</th>
                    <th className="px-4 py-3 text-right">Received by</th>
                    <th className="px-4 py-3 text-right">Price × Received (₹)</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-slate-100 hover:bg-indigo-50/30 cursor-pointer"
                      onClick={() => openRecipients(r)}
                    >
                      {(() => {
                        const rowPrice = Number(r.price) || 0;
                        const received = giftReceivedCounts[r.id] || 0;
                        const totalValue = rowPrice * received;
                        return (
                          <>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        <button
                          type="button"
                          className="text-left font-medium text-indigo-700 underline-offset-2 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRecipients(r);
                          }}
                        >
                          {r.giftName || "-"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{Number(r.pointsRequired) || 0}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                        {rowPrice.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-indigo-700 font-semibold">
                        <button
                          type="button"
                          className="underline-offset-2 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRecipients(r);
                          }}
                        >
                          {received}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">
                        {totalValue.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${
                            r.active === false
                              ? "bg-rose-100 text-rose-700"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {r.active === false ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => editRow(r)}
                            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(r)}
                            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            {r.active === false ? "Activate" : "Deactivate"}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteGift(r)}
                            className="rounded-lg border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-slate-300 bg-slate-50/90">
                  <tr>
                    <td className="px-4 py-3 font-bold text-slate-800">Total</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-800">—</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-800">
                      ₹{Math.round(totalGiftPrices).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-indigo-800">
                      {totalReceivedPeople}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-emerald-700">
                      ₹{Math.round(totalPriceIntoReceived).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          </section>
        </section>
      </main>

      {recipientGift
        ? createPortal(
            <div
              className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/55 p-4"
              role="presentation"
              onClick={closeRecipients}
            >
              <div
                className="flex h-[min(88vh,640px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-label={`${recipientGift.giftName || "Gift"} recipients`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Recipients</p>
                    <h2 className="text-lg font-bold text-slate-900">{recipientGift.giftName || "Gift"}</h2>
                    <p className="text-xs text-slate-500">
                      {Number(recipientGift.pointsRequired) || 0} pts needed ·{" "}
                      {(giftRecipientsById[recipientGift.id] || []).length} customer
                      {(giftRecipientsById[recipientGift.id] || []).length === 1 ? "" : "s"}
                      {recipientSearch.trim() ? ` · ${recipientList.length} shown` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeRecipients}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
                <div className="shrink-0 border-b border-slate-100 px-4 py-2">
                  <input
                    type="search"
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="Search name or phone"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="min-h-0 flex-1 overflow-auto bg-white">
                  {recipientList.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-500">
                      No customers currently qualify for this gift.
                    </p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-600">
                        <tr>
                          <th className="px-4 py-2 text-left">#</th>
                          <th className="px-4 py-2 text-left">Customer</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-right">Points</th>
                          <th className="px-4 py-2 text-right">Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipientList.map((c, i) => (
                          <tr key={`${c.name}-${i}`} className="border-t border-slate-100">
                            <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                            <td className="px-4 py-2 font-medium text-slate-900">{c.name}</td>
                            <td className="px-4 py-2 tabular-nums text-slate-700">{c.phone || "—"}</td>
                            <td className="px-4 py-2 text-right tabular-nums font-semibold text-indigo-700">
                              {Math.round(c.points)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-slate-600">{c.visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
